// Destructive action button — filled red. ALWAYS paired with
// <ConfirmDialog> per UI_GUIDELINES.md §3.1. A bare
// <DestructiveButton> that doesn't open a confirmation is a usage bug,
// not a missing feature.
//
// Visual: filled red (#dc2626 via bg-red-600), white text.
// States mirror PrimaryButton; colors differ. Same loading/disabled
// semantics.
//
// Accessibility: requires an explicit `aria-label` when the label
// isn't self-descriptive (e.g. a trash-can icon). The component does
// not synthesize one — silence-by-default is preferable to a wrong
// default label.

import { forwardRef } from 'react';

function Spinner({ className = 'h-4 w-4' }) {
  return (
    <span
      aria-hidden="true"
      className={`inline-block animate-spin rounded-full border-2 border-white/60 border-t-white ${className}`}
    />
  );
}

const DestructiveButton = forwardRef(function DestructiveButton(
  {
    children,
    loading = false,
    disabled = false,
    type = 'button',
    className = '',
    onClick,
    ...rest
  },
  ref,
) {
  const isDisabled = disabled || loading;
  return (
    <button
      ref={ref}
      type={type}
      disabled={isDisabled}
      onClick={onClick}
      aria-busy={loading || undefined}
      className={[
        'inline-flex items-center justify-center gap-2 rounded-md px-4 py-2',
        'text-sm font-medium text-white',
        'bg-red-600 hover:bg-red-700 active:bg-red-800',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2',
        'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-red-600 disabled:active:bg-red-600',
        'transition-colors',
        className,
      ].join(' ')}
      {...rest}
    >
      {loading && <Spinner />}
      <span>{children}</span>
    </button>
  );
});

export default DestructiveButton;
